import {
  ArrowsClockwiseIcon,
  BellIcon,
  BuildingOfficeIcon,
  ChartLineUpIcon,
  DownloadIcon,
  FunnelIcon,
  GridFourIcon,
  ListBulletsIcon,
  PackageIcon,
  PlusIcon,
  ShieldCheckIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import InventoryAdvancedSearch from '@/components/blocks/inventory/advanced-search';
import {
  ABCAnalysis,
  DemandForecast,
  ValuationReport,
} from '@/components/blocks/inventory/analytics';
import { BatchAlerts } from '@/components/blocks/inventory/batch-alerts';
import { BatchTracking } from '@/components/blocks/inventory/batch-tracking';
import { QualityManager } from '@/components/blocks/inventory/quality';
import RealTimeAlerts from '@/components/blocks/inventory/real-time-alerts';
import {
  StockManager,
  StockMonitor,
} from '@/components/blocks/inventory/stock';
import { SupplierIntegration } from '@/components/blocks/inventory/suppliers';
import {
  VariantManager,
  VariantSummary,
} from '@/components/blocks/inventory/variants';
import PageHeader from '@/components/blocks/page/header';
import MetricsDisplay from '@/components/blocks/shared/metrics-display';
import InventoryCreateSheet from '@/components/blocks/sheets/inventory-create';
import { CreateGuard, ViewGuard } from '@/components/guards/permission-guards';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import Container from '@/components/ui/container';
import { DataTable } from '@/components/ui/data-table';
import Row from '@/components/ui/row';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { showToast } from '@/components/ui/toast';
import { useGlobalState } from '@/contexts/GlobalStateContext';
import { useEnhancedApi } from '@/hooks/use-enhanced-api';
import { useInventory } from '@/hooks/use-inventory';
import { DataTableColumn } from '@/types/datatable';
import {
  InventoryItem,
  InventorySearchConfig,
  InventoryViewMode,
} from '@/types/inventory';
import { capitalizeFirstLetter, formatCurrency, formatDate } from '@/utils';
import { ACTIONS, RESOURCES, usePermissions } from '@/utils/permissions';

const Inventory = () => {
  const { showSheet } = useGlobalState();
  const permissions = usePermissions();
  const api = useEnhancedApi();

  // Use our comprehensive inventory hook
  const {
    items: products,
    metrics,
    stockAlerts,
    categories,
    loading,
    pagination,
    fetchItems,
    fetchMetrics,
    fetchStockAlerts,
    exportData,
    refreshData,
  } = useInventory();

  // UI state
  const [activeTab, setActiveTab] = useState<string>('inventory');
  const [viewMode, setViewMode] = useState<InventoryViewMode>('table');
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedProduct, _setSelectedProduct] = useState<InventoryItem | null>(
    null
  );
  const [suppliers, setSuppliers] = useState<
    Array<{ _id: string; businessName: string }>
  >([]);

  // Fetch suppliers for search filters
  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await api.suppliers.getAll({ limit: 100 });
      if (response.success && response.data) {
        // Handle both array and paginated response formats
        const suppliers = Array.isArray(response.data)
          ? response.data
          : response.data.suppliers || response.data.docs || [];
        setSuppliers(suppliers);
      }
    } catch {
      // Silently handle error - suppliers are optional for search filters
    }
  }, [api.suppliers]);

  // Initialize data
  useEffect(() => {
    const initializeData = async () => {
      await Promise.all([
        fetchItems(),
        fetchMetrics(),
        fetchStockAlerts(),
        fetchSuppliers(),
      ]);
    };

    initializeData();
  }, [fetchItems, fetchMetrics, fetchStockAlerts, fetchSuppliers]);

  // Product columns configuration
  const productColumns: DataTableColumn<InventoryItem>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Product Name',
        accessor: 'name',
        cell: ({ row }) => (
          <div>
            <div className='font-medium text-gray-90'>{row.original.name}</div>
            {row.original.description && (
              <div className='text-sm text-gray-50 mt-1 truncate max-w-xs'>
                {row.original.description}
              </div>
            )}
            {row.original.sku && (
              <div className='text-xs text-gray-40 mt-1'>
                SKU: {row.original.sku}
              </div>
            )}
          </div>
        ),
        sortable: true,
      },
      {
        id: 'category',
        header: 'Category',
        accessor: 'category',
        cell: ({ row }) => {
          const category = row.original.category;
          const categoryText =
            typeof category === 'string'
              ? category
              : category?.primary || 'N/A';
          return (
            <div className='flex flex-col gap-1'>
              <Badge variant='outline'>
                {capitalizeFirstLetter(categoryText)}
              </Badge>
              {typeof category === 'object' && category.secondary && (
                <Badge variant='secondary' className='text-xs'>
                  {capitalizeFirstLetter(category.secondary)}
                </Badge>
              )}
            </div>
          );
        },
        sortable: true,
      },
      {
        id: 'type',
        header: 'Type',
        accessor: 'isVariant',
        cell: ({ row }) => (
          <div className='flex flex-col gap-1'>
            <Badge variant={row.original.isVariant ? 'secondary' : 'outline'}>
              {row.original.isVariant ? 'Variant' : 'Simple'}
            </Badge>
            {row.original.quality?.grade && (
              <Badge variant='outline' className='text-xs'>
                Grade {row.original.quality.grade}
              </Badge>
            )}
          </div>
        ),
        sortable: true,
      },
      {
        id: 'stock',
        header: 'Stock',
        accessor: 'quantity',
        cell: ({ row }) => {
          const product = row.original;
          const totalQuantity =
            product.isVariant && product.variant
              ? product.variant.reduce(
                  (sum, v) => sum + (Number(v.quantity) || 0),
                  0
                )
              : Number(product.quantity) || 0;

          const reservedQty = product.reservedQuantity || 0;
          const availableQty = Math.max(0, totalQuantity - reservedQty);

          const reorderBaseline =
            product.isVariant && product.variant
              ? product.variant.reduce(
                  (sum, v) => sum + (Number(v.reorderLevel) || 0),
                  0
                )
              : Number(product.reorderLevel) || 0;

          const stockLevel =
            availableQty <= 0
              ? 'out'
              : availableQty <= reorderBaseline
                ? 'low'
                : 'good';
          const stockColor = {
            good: 'text-success-60',
            low: 'text-warning-60',
            out: 'text-destructive-60',
          }[stockLevel];

          const measurementUnit =
            typeof product.measurementUnit === 'string'
              ? product.measurementUnit
              : product.measurementUnit?.base || 'units';

          return (
            <div className='flex flex-col gap-1'>
              <div className='flex items-center space-x-2'>
                <span className={`font-medium ${stockColor}`}>
                  {totalQuantity}
                </span>
                <span className='text-gray-50 text-sm'>{measurementUnit}</span>
                {stockLevel !== 'good' && (
                  <WarningIcon className={`h-4 w-4 ${stockColor}`} />
                )}
              </div>
              {reservedQty > 0 && (
                <div className='text-xs text-orange-600'>
                  {reservedQty} reserved
                </div>
              )}
              {product.batchTracking?.enabled && (
                <div className='text-xs text-blue-600'>Batch tracked</div>
              )}
              {product.isVariant && product.variant?.length && (
                <div className='text-xs text-purple-600'>
                  {product.variant.length} variant
                  {product.variant.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          );
        },
        sortable: true,
      },
      {
        id: 'price',
        header: 'Price',
        accessor: 'price',
        cell: ({ row }) => {
          const product = row.original;

          if (!product.isVariant) {
            const price = product.pricing?.sellingPrice || product.price || 0;
            return (
              <div className='flex flex-col gap-1'>
                <span className='font-medium'>{formatCurrency(price)}</span>
                {product.pricing?.costPrice && (
                  <span className='text-xs text-gray-50'>
                    Cost: {formatCurrency(product.pricing.costPrice)}
                  </span>
                )}
              </div>
            );
          }

          const prices = (product.variant || [])
            .map(v => Number(v.price))
            .filter(v => !isNaN(v));
          if (prices.length === 0) {
            return <span className='text-gray-50'>—</span>;
          }
          const min = Math.min(...prices);
          const max = Math.max(...prices);
          return (
            <span className='font-medium'>
              {min === max
                ? formatCurrency(min)
                : `${formatCurrency(min)} - ${formatCurrency(max)}`}
            </span>
          );
        },
        sortable: true,
      },
      {
        id: 'status',
        header: 'Status',
        accessor: 'approvalStatus',
        cell: ({ row }) => {
          const status = row.original.approvalStatus || 'approved';
          const itemStatus = row.original.status || 'active';

          const statusColor = {
            approved: 'success',
            pending: 'warning',
            rejected: 'destructive',
          }[status] as 'success' | 'warning' | 'destructive';

          return (
            <div className='flex flex-col gap-1'>
              <Badge
                variant='outline'
                className={`text-${statusColor}-600 border-${statusColor}-200`}
              >
                {capitalizeFirstLetter(status)}
              </Badge>
              {itemStatus !== 'active' && (
                <Badge variant='secondary' className='text-xs'>
                  {capitalizeFirstLetter(itemStatus)}
                </Badge>
              )}
            </div>
          );
        },
        sortable: true,
      },
      {
        id: 'analytics',
        header: 'Performance',
        accessor: 'analytics',
        cell: ({ row }) => {
          const analytics = row.original.analytics;
          if (!analytics) return <span className='text-gray-50'>—</span>;

          return (
            <div className='flex flex-col gap-1 text-sm'>
              {analytics.totalRevenue && (
                <div className='text-success-600'>
                  Rev: {formatCurrency(analytics.totalRevenue)}
                </div>
              )}
              {analytics.turnoverRate && (
                <div className='text-blue-600'>
                  Turn: {analytics.turnoverRate.toFixed(1)}x
                </div>
              )}
              {analytics.abcClassification && (
                <Badge variant='outline' className='text-xs w-fit'>
                  ABC-{analytics.abcClassification}
                </Badge>
              )}
            </div>
          );
        },
      },
      {
        id: 'createdAt',
        header: 'Created',
        accessor: 'createdAt',
        cell: ({ row }) => (
          <div className='flex flex-col gap-1 text-sm'>
            <span>{formatDate(row.original.createdAt)}</span>
            {row.original.createdBy && (
              <span className='text-xs text-gray-50'>
                By: {row.original.createdBy}
              </span>
            )}
          </div>
        ),
        sortable: true,
      },
    ],
    []
  );

  // Row actions for products (only View Details)
  const productRowActions = useMemo(() => {
    const actions = [] as Array<{
      key: string;
      label: string;
      onClick: (product: InventoryItem) => void;
    }>;

    if (permissions.can(RESOURCES.INVENTORY, ACTIONS.READ)) {
      actions.push({
        key: 'view',
        label: 'View Details',
        onClick: (product: InventoryItem) => {
          window.location.href = `/inventory/${product._id}`;
        },
      });
    }

    return actions;
  }, [permissions]);

  // Handle advanced search
  const handleAdvancedSearch = useCallback(
    (searchConfig: InventorySearchConfig) => {
      fetchItems(searchConfig);
    },
    [fetchItems]
  );

  const handleClearSearch = useCallback(() => {
    fetchItems();
  }, [fetchItems]);

  const productCreateSheet = useCallback(() => {
    showSheet({
      id: 'create-product',
      type: 'create-product',
      title: 'Create Product',
      content: <InventoryCreateSheet onProductCreated={refreshData} />,
    });
  }, [showSheet, refreshData]);

  // Export inventory handler
  const handleExport = useCallback(async () => {
    if (isExporting) return;

    setIsExporting(true);
    try {
      const exportConfig = {
        format: 'excel',
        includeAnalytics: true,
        includeVariants: true,
      };

      await exportData(exportConfig);
    } catch {
      showToast('Export failed. Please try again.', false);
    } finally {
      setIsExporting(false);
    }
  }, [exportData, isExporting]);

  // Prepare metrics for MetricsDisplay component
  const metricsData = useMemo(() => {
    if (!metrics) return [];

    return [
      {
        id: 'totalProducts',
        title: 'Total Products',
        value: metrics.totalProducts,
        color: 'brand' as const,
      },
      {
        id: 'totalValue',
        title: 'Total Value',
        value: `$${(metrics.totalValue || 0).toLocaleString()}`,
        color: 'success' as const,
      },
      {
        id: 'lowStock',
        title: 'Low Stock',
        value: metrics.lowStock,
        color: 'warning' as const,
      },
      {
        id: 'outOfStock',
        title: 'Out of Stock',
        value: metrics.outOfStock,
        color: 'destructive' as const,
      },
      {
        id: 'pendingApproval',
        title: 'Pending Approval',
        value: metrics.pendingApproval,
        color: 'orange' as const,
      },
      {
        id: 'avgTurnover',
        title: 'Avg Turnover',
        value: `${(metrics.averageTurnover || 0).toFixed(1)}x`,
        color: 'brand' as const,
      },
    ];
  }, [metrics]);

  return (
    <ViewGuard
      resource={RESOURCES.INVENTORY}
      fallback={
        <Container className='space-y-6'>
          <div className='text-center py-12'>
            <PackageIcon className='h-12 w-12 text-gray-40 mx-auto mb-4' />
            <h3 className='text-lg font-semibold text-gray-90 mb-2'>
              Access Denied
            </h3>
            <p className='text-gray-60'>
              You don&apos;t have permission to view inventory. Contact your
              administrator for access.
            </p>
          </div>
        </Container>
      }
    >
      <Container className='space-y-6'>
        <Row>
          <PageHeader
            title='Inventory Management'
            subtitle='Advanced inventory tracking and analytics'
            showExport={permissions.can(RESOURCES.INVENTORY, ACTIONS.READ)}
            actions={
              <>
                <Button
                  hierarchy='outlined'
                  onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
                  className='flex items-center gap-2'
                >
                  <FunnelIcon className='h-4 w-4' />
                  {showAdvancedSearch ? 'Simple Search' : 'Advanced Search'}
                </Button>
                <Button
                  hierarchy='outlined'
                  onClick={refreshData}
                  disabled={loading}
                  className='flex items-center gap-2'
                >
                  <ArrowsClockwiseIcon
                    className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}
                  />
                  Refresh
                </Button>
                <ViewGuard resource={RESOURCES.INVENTORY}>
                  <Button
                    hierarchy='outlined'
                    onClick={handleExport}
                    disabled={isExporting}
                    className='flex items-center gap-2'
                  >
                    <DownloadIcon className='h-4 w-4' />
                    {isExporting ? 'Exporting...' : 'Export'}
                  </Button>
                </ViewGuard>
                <CreateGuard resource={RESOURCES.INVENTORY}>
                  <Button
                    onClick={productCreateSheet}
                    className='flex items-center gap-2'
                  >
                    <PlusIcon className='h-4 w-4' />
                    Create Product
                  </Button>
                </CreateGuard>
              </>
            }
          />
        </Row>

        {/* Metrics Dashboard */}
        <MetricsDisplay metrics={metricsData} />

        {/* Advanced Search (Collapsible) */}
        {showAdvancedSearch && (
          <Row>
            <InventoryAdvancedSearch
              onSearch={handleAdvancedSearch}
              onClear={handleClearSearch}
              loading={loading}
              suppliers={suppliers}
              categories={categories}
            />
          </Row>
        )}

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className='w-full'>
          <TabsList className='grid w-full grid-cols-9'>
            <TabsTrigger value='inventory' className='flex items-center gap-2'>
              <PackageIcon className='h-4 w-4' />
              Inventory
            </TabsTrigger>
            <TabsTrigger value='variants' className='flex items-center gap-2'>
              <GridFourIcon className='h-4 w-4' />
              Variants
            </TabsTrigger>
            <TabsTrigger value='stock' className='flex items-center gap-2'>
              <ArrowsClockwiseIcon className='h-4 w-4' />
              Stock
            </TabsTrigger>
            <TabsTrigger value='suppliers' className='flex items-center gap-2'>
              <BuildingOfficeIcon className='h-4 w-4' />
              Suppliers
            </TabsTrigger>
            <TabsTrigger value='quality' className='flex items-center gap-2'>
              <ShieldCheckIcon className='h-4 w-4' />
              Quality
            </TabsTrigger>
            <TabsTrigger
              value='batch-tracking'
              className='flex items-center gap-2'
            >
              <ListBulletsIcon className='h-4 w-4' />
              Batches
            </TabsTrigger>
            <TabsTrigger value='alerts' className='flex items-center gap-2'>
              <BellIcon className='h-4 w-4' />
              Alerts
              {stockAlerts.length > 0 && (
                <Badge variant='destructive' className='ml-1 px-1 py-0 text-xs'>
                  {stockAlerts.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value='analytics' className='flex items-center gap-2'>
              <ChartLineUpIcon className='h-4 w-4' />
              Analytics
            </TabsTrigger>
            <TabsTrigger value='reports' className='flex items-center gap-2'>
              <DownloadIcon className='h-4 w-4' />
              Reports
            </TabsTrigger>
          </TabsList>

          {/* Inventory Tab */}
          <TabsContent value='inventory' className='space-y-4'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <h3 className='text-lg font-semibold'>Products Catalog</h3>
                {products.length > 0 && (
                  <Badge variant='secondary'>{products.length} items</Badge>
                )}
              </div>
              <div className='flex items-center gap-2'>
                <Button
                  hierarchy='outlined'
                  size='sm'
                  onClick={() =>
                    setViewMode(viewMode === 'table' ? 'grid' : 'table')
                  }
                >
                  {viewMode === 'table' ? (
                    <GridFourIcon className='h-4 w-4' />
                  ) : (
                    <ListBulletsIcon className='h-4 w-4' />
                  )}
                </Button>
              </div>
            </div>

            <DataTable
              data={products}
              columns={productColumns}
              loading={loading}
              pagination={pagination}
              rowActions={productRowActions}
              enableSorting={true}
              enableColumnVisibility={true}
              enablePagination={true}
              emptyConfig={{
                title: 'No Products Found',
                description:
                  'Get started by adding your first product to build your inventory.',
                icon: <PackageIcon className='h-12 w-12 text-gray-40' />,
              }}
              className='rounded-lg border'
            />
          </TabsContent>

          {/* Variants Tab */}
          <TabsContent value='variants' className='space-y-4'>
            <div className='space-y-6'>
              {/* Variants Overview */}
              <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                {products
                  .filter(
                    product => product.isVariant && product.variant?.length
                  )
                  .slice(0, 6)
                  .map(product => (
                    <VariantSummary
                      key={product._id}
                      inventoryItem={product}
                      showDetails={true}
                    />
                  ))}
                {products.filter(
                  product => product.isVariant && product.variant?.length
                ).length === 0 && (
                  <Card className='col-span-full'>
                    <CardContent className='p-6 text-center'>
                      <GridFourIcon className='h-12 w-12 text-gray-400 mx-auto mb-4' />
                      <h3 className='font-medium text-gray-900 mb-2'>
                        No Product Variants Found
                      </h3>
                      <p className='text-gray-600'>
                        Create product variants to track different sizes,
                        colors, or specifications.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Individual Variant Management for selected items */}
              {selectedProduct && selectedProduct.isVariant && (
                <Card>
                  <CardHeader>
                    <CardTitle>
                      Variant Management - {selectedProduct.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <VariantManager
                      inventoryItem={selectedProduct}
                      onVariantUpdate={() => {
                        refreshData();
                        showToast('Variants updated successfully', true);
                      }}
                    />
                  </CardContent>
                </Card>
              )}

              {!selectedProduct && (
                <Card>
                  <CardContent className='p-6 text-center'>
                    <GridFourIcon className='h-12 w-12 text-gray-400 mx-auto mb-4' />
                    <h3 className='font-medium text-gray-900 mb-2'>
                      No Item Selected
                    </h3>
                    <p className='text-gray-600'>
                      Select an inventory item with variants enabled from the
                      Inventory tab to manage its variants.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Stock Management Tab */}
          <TabsContent value='stock' className='space-y-4'>
            <div className='space-y-6'>
              {/* Real-time Stock Monitor */}
              <StockMonitor
                inventoryItems={products}
                autoConnect={true}
                showAlerts={true}
                maxDisplayItems={25}
              />

              {/* Individual Stock Management for selected items */}
              {selectedProduct && (
                <Card>
                  <CardHeader>
                    <CardTitle>
                      Stock Management - {selectedProduct.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <StockManager
                      inventoryItem={selectedProduct}
                      onStockUpdate={() => {
                        refreshData();
                        showToast('Stock updated successfully', true);
                      }}
                    />
                  </CardContent>
                </Card>
              )}

              {!selectedProduct && (
                <Card>
                  <CardContent className='p-6 text-center'>
                    <ArrowsClockwiseIcon className='h-12 w-12 text-gray-400 mx-auto mb-4' />
                    <h3 className='font-medium text-gray-900 mb-2'>
                      No Item Selected
                    </h3>
                    <p className='text-gray-600'>
                      Select an inventory item from the Inventory tab to manage
                      its stock reservations and adjustments.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Suppliers Tab */}
          <TabsContent value='suppliers' className='space-y-4'>
            <div className='space-y-6'>
              {/* Individual Supplier Management for selected items */}
              {selectedProduct && (
                <Card>
                  <CardHeader>
                    <CardTitle>
                      Supplier Management - {selectedProduct.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <SupplierIntegration
                      inventoryItem={selectedProduct}
                      onUpdate={() => {
                        refreshData();
                        showToast('Suppliers updated successfully', true);
                      }}
                    />
                  </CardContent>
                </Card>
              )}

              {!selectedProduct && (
                <Card>
                  <CardContent className='p-6 text-center'>
                    <BuildingOfficeIcon className='h-12 w-12 text-gray-400 mx-auto mb-4' />
                    <h3 className='font-medium text-gray-900 mb-2'>
                      No Item Selected
                    </h3>
                    <p className='text-gray-600'>
                      Select an inventory item from the Inventory tab to manage
                      its supplier relationships and purchase history.
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Supplier Overview Cards */}
              <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                {products
                  .filter(
                    product => product.suppliers && product.suppliers.length > 0
                  )
                  .slice(0, 6)
                  .map(product => {
                    const primarySupplier = product.suppliers?.find(
                      s => s.isPrimary
                    );
                    const supplierInfo = primarySupplier
                      ? `Supplier ID: ${primarySupplier.supplier}`
                      : 'No Primary Supplier';

                    return (
                      <Card key={product._id}>
                        <CardContent className='p-4'>
                          <div className='flex items-center gap-3'>
                            <BuildingOfficeIcon className='h-8 w-8 text-blue-500' />
                            <div className='flex-1'>
                              <div className='font-medium text-gray-900'>
                                {product.name}
                              </div>
                              <div className='text-sm text-gray-600'>
                                {supplierInfo}
                              </div>
                              <div className='text-xs text-blue-600'>
                                {product.suppliers!.length} supplier
                                {product.suppliers!.length !== 1 ? 's' : ''}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}

                {products.filter(
                  product => product.suppliers && product.suppliers.length > 0
                ).length === 0 && (
                  <Card className='col-span-full'>
                    <CardContent className='p-6 text-center'>
                      <BuildingOfficeIcon className='h-12 w-12 text-gray-400 mx-auto mb-4' />
                      <h3 className='font-medium text-gray-900 mb-2'>
                        No Supplier Relationships Found
                      </h3>
                      <p className='text-gray-600'>
                        Add supplier relationships to inventory items to track
                        pricing, lead times, and purchase history.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Quality Tab */}
          <TabsContent value='quality' className='space-y-4'>
            <div className='space-y-6'>
              {/* Individual Quality Management for selected items */}
              {selectedProduct && (
                <Card>
                  <CardHeader>
                    <CardTitle>
                      Quality Management - {selectedProduct.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <QualityManager
                      inventoryItem={selectedProduct}
                      onUpdate={() => {
                        refreshData();
                        showToast(
                          'Quality information updated successfully',
                          true
                        );
                      }}
                    />
                  </CardContent>
                </Card>
              )}

              {!selectedProduct && (
                <Card>
                  <CardContent className='p-6 text-center'>
                    <ShieldCheckIcon className='h-12 w-12 text-gray-400 mx-auto mb-4' />
                    <h3 className='font-medium text-gray-900 mb-2'>
                      No Item Selected
                    </h3>
                    <p className='text-gray-600'>
                      Select an inventory item from the Inventory tab to manage
                      its quality grades, certifications, and inspection
                      records.
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Quality Overview Cards */}
              <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                {products
                  .filter(
                    product =>
                      product.quality?.grade ||
                      product.quality?.certifications?.length
                  )
                  .slice(0, 6)
                  .map(product => {
                    const qualityGrade = product.quality?.grade || 'Not Set';
                    const certificationCount =
                      product.quality?.certifications?.length || 0;

                    return (
                      <Card key={product._id}>
                        <CardContent className='p-4'>
                          <div className='flex items-center gap-3'>
                            <ShieldCheckIcon className='h-8 w-8 text-green-500' />
                            <div className='flex-1'>
                              <div className='font-medium text-gray-900'>
                                {product.name}
                              </div>
                              <div className='text-sm text-gray-600'>
                                Grade: {qualityGrade}
                              </div>
                              <div className='text-xs text-green-600'>
                                {certificationCount} certification
                                {certificationCount !== 1 ? 's' : ''}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}

                {products.filter(
                  product =>
                    product.quality?.grade ||
                    product.quality?.certifications?.length
                ).length === 0 && (
                  <Card className='col-span-full'>
                    <CardContent className='p-6 text-center'>
                      <ShieldCheckIcon className='h-12 w-12 text-gray-400 mx-auto mb-4' />
                      <h3 className='font-medium text-gray-900 mb-2'>
                        No Quality Information Found
                      </h3>
                      <p className='text-gray-600'>
                        Add quality grades, certifications, and inspection
                        records to track product quality standards.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Batch Tracking Tab */}
          <TabsContent value='batch-tracking' className='space-y-4'>
            <div className='space-y-6'>
              {/* Batch Alerts */}
              <BatchAlerts
                inventoryItems={products}
                onViewBatch={(_inventoryId, batchNumber) => {
                  // Navigate to specific batch or show batch details
                  showToast(`Viewing batch ${batchNumber}`, true);
                }}
              />

              {/* Individual Batch Tracking for selected items */}
              {selectedProduct && (
                <Card>
                  <CardHeader>
                    <CardTitle>
                      Batch Tracking - {selectedProduct.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <BatchTracking
                      inventoryItem={selectedProduct}
                      onBatchUpdate={() => {
                        refreshData();
                        showToast('Batch updated successfully', true);
                      }}
                    />
                  </CardContent>
                </Card>
              )}

              {!selectedProduct && (
                <Card>
                  <CardContent className='p-6 text-center'>
                    <ListBulletsIcon className='h-12 w-12 text-gray-400 mx-auto mb-4' />
                    <h3 className='font-medium text-gray-900 mb-2'>
                      No Item Selected
                    </h3>
                    <p className='text-gray-600'>
                      Select an inventory item from the Inventory tab to manage
                      its batches.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value='alerts' className='space-y-4'>
            <div className='grid grid-cols-1 gap-6'>
              <RealTimeAlerts autoConnect={true} showMovements={true} />
              <Card>
                <CardHeader>
                  <CardTitle className='flex items-center gap-2'>
                    <ChartLineUpIcon className='h-5 w-5' />
                    Quick Stats
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='grid grid-cols-2 gap-4'>
                    <div className='text-center p-4 bg-gray-5 rounded-lg'>
                      <div className='text-2xl font-bold text-warning-60'>
                        {metrics?.lowStock || 0}
                      </div>
                      <div className='text-sm text-gray-60'>
                        Low Stock Items
                      </div>
                    </div>
                    <div className='text-center p-4 bg-gray-5 rounded-lg'>
                      <div className='text-2xl font-bold text-destructive-60'>
                        {metrics?.outOfStock || 0}
                      </div>
                      <div className='text-sm text-gray-60'>Out of Stock</div>
                    </div>
                    <div className='text-center p-4 bg-gray-5 rounded-lg'>
                      <div className='text-2xl font-bold text-orange-600'>
                        {metrics?.pendingApproval || 0}
                      </div>
                      <div className='text-sm text-gray-60'>
                        Pending Approval
                      </div>
                    </div>
                    <div className='text-center p-4 bg-gray-5 rounded-lg'>
                      <div className='text-2xl font-bold text-blue-600'>
                        {metrics?.expiringBatches || 0}
                      </div>
                      <div className='text-sm text-gray-60'>
                        Expiring Batches
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value='analytics' className='space-y-6'>
            <div className='grid gap-6'>
              {/* ABC Analysis */}
              <ABCAnalysis period='30d' />

              {/* Demand Forecasting */}
              <DemandForecast period='90d' horizon='30d' />

              {/* Valuation Report */}
              <ValuationReport method='Weighted Average' />
            </div>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value='reports' className='space-y-4'>
            <Card>
              <CardHeader>
                <CardTitle>Export & Reports</CardTitle>
              </CardHeader>
              <CardContent>
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
                  <Button
                    hierarchy='outlined'
                    className='h-20 flex-col gap-2'
                    onClick={handleExport}
                    disabled={isExporting}
                  >
                    <DownloadIcon className='h-6 w-6' />
                    <span>Export Inventory</span>
                  </Button>
                  <Button
                    hierarchy='outlined'
                    className='h-20 flex-col gap-2'
                    disabled
                  >
                    <ChartLineUpIcon className='h-6 w-6' />
                    <span>Analytics Report</span>
                  </Button>
                  <Button
                    hierarchy='outlined'
                    className='h-20 flex-col gap-2'
                    disabled
                  >
                    <BellIcon className='h-6 w-6' />
                    <span>Alerts Report</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </Container>
    </ViewGuard>
  );
};

export default Inventory;
